/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * RoadmapSection — phased timeline with KPIs.
 */
import { motion } from "framer-motion";
import { Rocket, ArrowRight, Target } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";

const phaseColors = [
  { accent: "text-[#4FA9A1]", border: "border-[#4FA9A1]/30", bg: "bg-[#4FA9A1]/10", dot: "bg-[#4FA9A1]" },
  { accent: "text-[#3B82F6]", border: "border-[#3B82F6]/30", bg: "bg-[#3B82F6]/10", dot: "bg-[#3B82F6]" },
  { accent: "text-[#A78BFA]", border: "border-[#A78BFA]/30", bg: "bg-[#A78BFA]/10", dot: "bg-[#A78BFA]" },
];

export default function RoadmapSection({ data }) {
  return (
    <PitchSection id="roadmap" dark>
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
        {data.title}
      </h2>

      <div className="relative">
        {/* Vertical line — centre on desktop, left edge on mobile */}
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#4FA9A1]/40 via-[#3B82F6]/40 to-[#A78BFA]/40" />

        <div className="space-y-4 md:space-y-6">
          {data.phases.map((phase, i) => {
            const color = phaseColors[i];
            const isLeft = i % 2 === 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className={`relative flex flex-col md:flex-row ${isLeft ? "" : "md:flex-row-reverse"} items-start md:items-center gap-6`}
              >
                {/* Timeline dot */}
                <div className="flex md:hidden absolute left-4 -translate-x-1/2 top-4 w-3 h-3 rounded-full border-2 border-white/20 z-10">
                  <div className={`w-full h-full rounded-full ${color.dot}`} />
                </div>
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-white/20 z-10">
                  <div className={`w-full h-full rounded-full ${color.dot}`} />
                </div>

                {/* Card */}
                <div className={`pl-8 md:pl-0 md:w-[calc(50%-2rem)] ${isLeft ? "md:mr-auto" : "md:ml-auto"}`}>
                  <div className={`${color.bg} ${color.border} border rounded-2xl p-4`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Rocket className={`w-4 h-4 ${color.accent}`} />
                      <span className={`text-base font-bold ${color.accent}`}>{phase.label}</span>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">{phase.subtitle}</p>

                    <ul className="space-y-1 mb-3">
                      {phase.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                          <ArrowRight className={`w-3.5 h-3.5 ${color.accent} mt-0.5 flex-shrink-0`} />
                          {item}
                        </li>
                      ))}
                    </ul>

                    {phase.kpis && (
                      <div className="flex items-start gap-2 bg-black/20 rounded-lg p-2 border border-white/5">
                        <Target className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-gray-400 leading-relaxed">
                          <span className="text-gray-300 font-medium">KPIs: </span>
                          {phase.kpis}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </PitchSection>
  );
}
